/**
 * Order-independent JSON, which every content digest and every structural
 * comparison in the codebase is taken over.
 *
 * @module utils/stable-json
 */

/** How sibling object keys are ordered before they are serialised. */
export type CompareKeys = (left: string, right: string) => number;

/**
 * Order two strings by UTF-16 code unit.
 *
 * The default, and the only ordering two processes are guaranteed to agree on.
 *
 * @param left - One key.
 * @param right - The other.
 * @returns Negative, zero or positive, as a comparator.
 */
export function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : Number(left > right);
}

/**
 * Order two strings by the runtime's collation.
 *
 * Depends on the ICU build, so two API replicas can order the same keys
 * differently and derive different digests from identical content. Reserved
 * for callers whose stored digests were taken over this ordering and cannot be
 * recomputed, which is `canonicalGovernanceJson` alone.
 *
 * @param left - One key.
 * @param right - The other.
 * @returns Negative, zero or positive, as a comparator.
 */
export function compareCollated(left: string, right: string): number {
  return left.localeCompare(right);
}

/** How one call to {@link stableJson} treats ordering and unrepresentable values. */
export interface StableJsonOptions {
  /** Sibling key ordering. Defaults to {@link compareCodeUnits}. */
  compareKeys?: CompareKeys;
  /**
   * Reject values `JSON.stringify` would coerce, drop or throw over, naming
   * where in the document they sit. A digest bound to content wants the throw,
   * because a digest over a coerced value hides the fault it was taken to
   * catch. A comparison between two in-memory values does not, and serialises
   * whatever it was handed.
   */
  validate?: boolean;
}

/** The resolved form of {@link StableJsonOptions}, threaded through the recursion. */
type Rules = Required<StableJsonOptions>;

function canonicalise(value: unknown, rules: Rules, path: string): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (rules.validate && !Number.isFinite(value)) {
      throw new TypeError(`Cannot canonicalise non-finite number at ${path}.`);
    }
    // Negative zero serialises as `0`, so normalising it costs nothing and
    // keeps the canonical form free of a distinction JSON cannot carry.
    return Object.is(value, -0) ? 0 : value;
  }
  if (Array.isArray(value)) {
    return value.map((entry, index) =>
      entry === undefined
        ? null
        : canonicalise(entry, rules, rules.validate ? `${path}[${index}]` : path),
    );
  }
  if (typeof value === "object") {
    const prototype = Object.getPrototypeOf(value);
    if (rules.validate && prototype !== Object.prototype && prototype !== null) {
      throw new TypeError(`Cannot canonicalise non-plain object at ${path}.`);
    }
    const record = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(record)
        .sort(rules.compareKeys)
        .filter((key) => record[key] !== undefined)
        .map((key) => [
          key,
          canonicalise(record[key], rules, rules.validate ? `${path}.${key}` : path),
        ]),
    );
  }
  if (rules.validate) {
    throw new TypeError(`Cannot canonicalise ${typeof value} at ${path}.`);
  }
  return value;
}

/**
 * Serialise a value with object keys recursively ordered, so two values that
 * differ only in key insertion order produce the same string.
 *
 * @param value - The value to serialise.
 * @param options - Ordering and validation for this call.
 * @returns The stable JSON text.
 * @throws {TypeError} Under `validate`, when a value cannot form deterministic
 *   JSON, naming the path it sits at.
 */
export function stableJson(value: unknown, options: StableJsonOptions = {}): string {
  const rules: Rules = {
    compareKeys: options.compareKeys ?? compareCodeUnits,
    validate: options.validate ?? false,
  };
  // `JSON.stringify` answers `undefined` for a top-level undefined, function
  // or symbol, which only lenient mode lets through. Those are absent values,
  // and `null` is how this representation already spells one.
  return JSON.stringify(canonicalise(value, rules, "$")) ?? "null";
}
